import ApplicationEntryRouteConstants from 'ApplicationEntry/ApplicationEntryRouteConstants';
import {addPrimaryBeneficiary, updatePrimaryBeneficiary} from 'ApplicationEntry/ducks/primaryBeneficiaryList';
import {PhoneNumber} from 'ApplicationEntry/models/PhoneNumber';
import {
  EmptyPrimaryBeneficiary,
  PrimaryBeneficiary,
  PrimaryBeneficiaryActionType,
} from 'ApplicationEntry/models/PrimaryBeneficiary';
import {MAX_PRIMARY_BENEFICIARIES} from 'ApplicationEntry/models/PrimaryBeneficiaryList';
import * as I from 'immutable';
import React, {useReducer} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Redirect} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import Loading from 'Shared/components/Loading';
import './PrimaryBeneficiary.sass';
import PrimaryBeneficiaryData from './PrimaryBeneficiaryData';
import * as PBR from './PrimaryBeneficiaryReducer';

import {
  AppBar,
  Button,
  Container,
  createMuiTheme,
  Dialog,
  Grid,
  IconButton,
  ThemeProvider,
  Toolbar,
  Typography,
} from '@material-ui/core';
import {green} from '@material-ui/core/colors';
import CloseIcon from '@material-ui/icons/Close';
import {matchApplication, InvalidApplication, ValidApplication} from 'ApplicationEntry/models/Application';
import {InvalidModalApplication, ValidModalApplication} from 'ApplicationEntry/models/ModalApplication';
import {ZipCode} from 'ApplicationEntry/models/ZipCode';

const theme = createMuiTheme({
  palette: {
    primary: green,
  },
});

type DialogProps = {
  dialogOpen: boolean;
  dialogClose: () => void;
  dialogTitle: string;
  dialogSubTitle: string;
  dialogAdditional: string;
  primaryBeneficiaryIndex: number;
  editPrimaryBeneficiary: boolean;
};

const PrimaryBeneficiaryDialog = (props: DialogProps) => {
  const applicationState = useSelector((state: ReducerState) => state.application);

  const initialState: PBR.State = {members: I.List<PrimaryBeneficiary>().push(EmptyPrimaryBeneficiary)};
  const [localState, dispatch] = useReducer(PBR.reducer, initialState);
  const dispatchRedux = useDispatch();
  let disableSave = false;

  const application = (applicationState as InvalidModalApplication | ValidModalApplication).application as
    | InvalidApplication
    | ValidApplication;

  if (applicationState.key === 'LoadingModalApplication') {
    return <Loading />;
  } else if (applicationState.key === 'UndeterminedModalApplication') {
    return <Redirect to={ApplicationEntryRouteConstants.WorkQueue} />;
  }

  const primaryBeneficiaryList = matchApplication(application, {
    Valid: valid => valid.primaryBeneficiaryList,
    Invalid: invalid => invalid.primaryBeneficiaryList,
    Undetermined: () => undefined,
  });

  if (props.editPrimaryBeneficiary && localState.members.toArray()[0].key === 'UnvalidPrimaryBeneficiary') {
    dispatch(
      PBR.editPrimaryBeneficiary(
        primaryBeneficiaryList!.primaryBeneficiaryList.toArray()[props.primaryBeneficiaryIndex]
      )
    );
  }

  const checkTotalPercent = () => {
    let _totalPercentage =
      primaryBeneficiaryList!.primaryBeneficiaryList.reduce((a, b) => a + b.percent, 0) +
      localState.members.reduce((a, b) => a + b.percent, 0);
    return props.editPrimaryBeneficiary
      ? _totalPercentage -
          primaryBeneficiaryList!.primaryBeneficiaryList.toArray()[props.primaryBeneficiaryIndex].percent
      : _totalPercentage;
  };

  const allowSaveOption = (beneficiary: PrimaryBeneficiary) =>
    beneficiary.key !== 'ValidPrimaryBeneficiary' || checkTotalPercent() > 100;

  disableSave = localState.members.some(allowSaveOption);

  const handleSave = () => {
    props.editPrimaryBeneficiary
      ? dispatchRedux(updatePrimaryBeneficiary(localState.members.toArray()[0], props.primaryBeneficiaryIndex))
      : dispatchRedux(addPrimaryBeneficiary(localState.members));
    dispatch(PBR.emptyPrimaryBeneficiary());
    props.dialogClose();
  };

  const handleCancel = () => {
    dispatch(PBR.emptyPrimaryBeneficiary());
    props.dialogClose();
  };

  return (
    <Dialog fullScreen open={props.dialogOpen} onClose={props.dialogClose}>
      <AppBar className="primary-beneficiary-section__dialog-header">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={props.dialogClose} aria-label="close">
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" className="primary-beneficiary-section__dialog-title">
            {props.dialogTitle}
          </Typography>
        </Toolbar>
      </AppBar>

      <div className="primary-beneficiary-section__dialog ">
        <Container 
           className="primary-beneficiary-section__dialog-container" 
           maxWidth={false}
        >
          <div className="primary-beneficiary-section__dialog-container__title-container">
            <Typography
              variant="subtitle1"
              className="primary-beneficiary-section__dialog-container__title-container-title"
              color="primary"
            >
              {props.dialogSubTitle}
            </Typography>
          </div>

          {localState.members.map((ele, i) => (
            <PrimaryBeneficiaryData
              key={i}
              index={i}
              primaryBeneficiaryIndex={props.primaryBeneficiaryIndex}
              editPrimaryBeneficiary={props.editPrimaryBeneficiary}
              removePrimaryBeneficiary={() => dispatch(PBR.removePrimaryBeneficiary(i))}
              changeFirstName={(value: string) => dispatch(PBR.updateFirstName(i, value))}
              changeMiddleName={(value: string) => dispatch(PBR.updateMiddleName(i, value))}
              changeLastName={(value: string) => dispatch(PBR.updateLastName(i, value))}
              changeRelationship={(value: string) => dispatch(PBR.updateRelationship(i, value))}
              changeDOB={(value: Date | '') => dispatch(PBR.updateDOB(i, value))}
              changeSSN={(value: string) => dispatch(PBR.updateSSN(i, value))}
              changePercent={(value: number) => dispatch(PBR.updatePercent(i, value))}
              changeAddress={(value: string) => dispatch(PBR.updateAddress(i, value))}
              changeAddressLine2={(value: string) => dispatch(PBR.updateAddressLine2(i, value))}
              changeCity={(value: string) => dispatch(PBR.updateCity(i, value))}
              changeState={(value: string) => dispatch(PBR.updateState(i, value))}
              changeZip={(value: ZipCode) => dispatch(PBR.updateZip(i, value))}
              changePhone={(value: PhoneNumber) => dispatch(PBR.updatePhone(i, value))}
              changeSignatureDate={(value: Date | '') => dispatch(PBR.updateSignatureDate(i, value))}
              members={ele}
              primaryBeneficiaryListLength={primaryBeneficiaryList!.primaryBeneficiaryList.size}
              totalPercent={checkTotalPercent()}
              modalApplication={applicationState}
            />
          ))}
          {!props.editPrimaryBeneficiary &&
          primaryBeneficiaryList!.primaryBeneficiaryList.size + localState.members.size < MAX_PRIMARY_BENEFICIARIES &&
          checkTotalPercent() < 100 ? (
            <>
              <Grid
                container
                direction="row"
                justify="center"
                alignItems="center"
                className="primary-beneficiary-section__additonal-dependent"
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => dispatch(PBR.addPrimaryBeneficiary())}
                >
                  <Typography variant="h6">+</Typography>
                  {props.dialogAdditional}
                </Button>
              </Grid>
            </>
          ) : null}

          <div className="primary-beneficiary-section__buttons-container">
            <Button
              variant="contained"
              color="inherit"
              onClick={handleCancel}
              className="primary-beneficiary-section__buttons"
            >
              Cancel
            </Button>
            <ThemeProvider theme={theme}>
              <Button
                variant="contained"
                color="primary"
                className="primary-beneficiary-section__buttons"
                onClick={handleSave}
                disabled={disableSave}
              >
                Save Changes
              </Button>
            </ThemeProvider>
          </div>
        </Container>
      </div>
    </Dialog>
  );
};

export default PrimaryBeneficiaryDialog;
